import React, { useCallback, useEffect, useMemo, useState } from "react";

// API
import api from "../../../api/api";

// Components
import StackStyle from "../../Styled/Stack.style";
import Modal from "../../Modal/Modal";
import QueueStyle from "../../Styled/Queue.style";
import Checkbox from "../../Controls/Checkbox";
import SelectableBadge from "../../Controls/SelectableBadge";
import { ModelSelect } from "../../Controls/ModelSelect";
import { RangeSelect } from "../../Controls/RangeSelect";
import { BrandSelect } from "../../Controls/BrandSelect";
import { VINInput } from "../../Controls/VINInput";
import { MMCodeInput } from "../../Controls/MMCodeInput";
import { SubmitButton } from "../../Controls/SubmitButton";
import SearchModal from "../Search/SearchModal";

// Utils
import useType, { TYPES } from "../../../utils/hooks/useType";
import useAsync from "../../../utils/hooks/useAsync";
import hasNoValue from "../../../utils/helpers/hasNoValue";
import {
  CAR_CONDITIONS,
  COLOUR_TYPES,
  CONDITIONS,
  SCOPES,
  USER_KEY,
  RATE_TYPES,
  IMAGES_ENDPOINT,
  TRANSMISSION_TYPES,
  FUEL_TYPES,
} from "../../../utils/constants/SYSTEM";
import { get } from "../../../utils/helpers/storage";
import { CurrencyInput } from "../../Controls/CurrencyInput";
import { SelectInput } from "../../Controls/SelectInput";
import { ArrowSquareOut, Camera, ChatCircle, Circle, Info, Link, Lock, MarkerCircle, Palette } from "phosphor-react";
import { NumberInput } from "../../Controls/NumberInput";
import { TextAreaInput } from "../../Controls/TextAreaInput";
import styled from "styled-components";
import DateTimeInput from "../../Controls/DateTimeInput";
import getDate from "../../../utils/helpers/getDate";
import DateInput from "../../Controls/DateInput";
import ScrollEffect from "../../../effects/ScrollEffect";
import { InputDisabled } from "../../Controls/InputDisabled";
import parseDecimal from "../../../utils/helpers/parseDecimal";
import { CurrencyInputDisabled } from "../../Controls/CurrencyInputDisabled";
import moment from "moment/moment";
import {
  useModalHistory,
  ACTIONS as MODAL_ACTIONS,
} from "../../../context/ModalHistoryProvider";
import {
  useDealsContext,
  ACTIONS as DEAL_ACTIONS,
} from "../../../context/DealsProvider";
import { useRef } from "react";
import UsedCarImageGallery from "../../Popups/ImageGallery";
import { CancelButton } from "../../Controls/CancelButton";

import DisplayOnWebsiteWarning from "../../Popups/DisplayOnWebsiteWarning";
import convertImageToByteArray from "../../../utils/helpers/convertImagesToByteArray";
import { ImagesInput } from "../../Controls/ImagesInput";
import calculateTotalCostOfCredit from "../../../utils/helpers/calculateTotalCostOfCredit";
import useToast, { TOAST_TYPES } from "../../../utils/hooks/useToast";
import { getObjectDiff } from "../../../utils/helpers/findObjectDifferences";
import colors from "../../../utils/constants/COLORS";
import { IconButton, Tooltip } from "@mui/material";
import DealAuditHistory from "../../Popups/DealAuditHistory";
import { ModelSelectUsed } from "../../Controls/ModelSelectUsed";
import validateVinNo from "../../../utils/helpers/validateVinNo";
import convertNumberToCurrency from "../../../utils/helpers/convertNumberToCurrency";
import StandardImageList from "../../ImageList/ImageList";
import { useNavigate } from "react-router";
import useUpdateEffectOnce from "../../../utils/hooks/useUpdateEffectOnce";

export default function CreateEditUsedDealModal({
  scope,
  dealCondition,
  dealInfo,
}) {
  const navigate = useNavigate();
  const user = get(USER_KEY);
  const today = moment();
  today.add(2, "months");
  const lastDayOfNextMonth = new Date(today.year(), today.month(), 0); // 1 Month ahead, last day of month
  const [, startDateInit] = getDate(new Date());
  const [demoAvailableToSellDateInit] = getDate(new Date());
  const [doNotSellDateInit] = getDate(new Date());
  const [, endDateInit] = getDate(lastDayOfNextMonth);
  const [listDateInit] = getDate(new Date());
  const [firstRegDateInit] = getDate(new Date());

  // const [reconCost, setReconCost] = useType("number", null);
  // const [reconTime, setReconTime] = useType("number", null);
  // const [activeDeal, setActiveDeal] = useType("boolean", false);
  const [mmCode, setMMCode] = useType("string", "");
  const [vin, setVin] = useType("string", "");
  const [brand, setBrand] = useType("string", "");
  const [range, setRange] = useType("string", "");
  const [model, setModel] = useType("string", "");
  const [dealAssistance, setDealAssistance] = useType("number", null);
  const [reportedToOEM, setReportedToOEM] = useType("boolean", false);
  const [photosTaken, setPhotosTaken] = useType("boolean", false);
  const [photosEdited, setPhotosEdited] = useType("boolean", false);
  const [check101Done, setCheck101Done] = useType("boolean", false);
  const [reconWorkDone, setReconWorkDone] = useType("boolean", false);
  const [doNotSell, setDoNotSell] = useType("boolean", false);
  const [doNotSellEndDate, setDoNotSellEndDate] = useState(doNotSellDateInit);
  const [doNotSellEndDateUI, setDoNotSellEndDateUI] = useState(doNotSellDateInit);
  const [doNotSellReason, setDoNotSellReason] = useType("string", "");
  const [notArrivedYet, setNotArrivedYet] = useType("boolean", false);
  const [unclaimedRebates, setUnclaimedRebates] = useType("number", null);
  const [autoTraderPriceInclVAT, setAutoTraderPriceInclVAT] = useType("number", null);
  const [autoTraderLink, setAutoTraderLink] = useType("string", "");
  const [demoAvailableToSellDate, setDemoAvailableToSellDate] = useState(demoAvailableToSellDateInit);
  const [demoAvailableToSellDateUI, setDemoAvailableToSellDateUI] = useState(demoAvailableToSellDateInit);
  const [ratePercent, setRatePercent] = useType("number", null);
  const [rateType, setRateType] = useType("string", "");
  const [deposit, setDeposit] = useType("number", null);
  const [period, setPeriod] = useType("number", null);
  const [balloonPercent, setBalloonPercent] = useType("number", 35);
  const [balloonAmount, setBalloonAmount] = useType("number", null);
  const [totalCostOfCredit, setTotalCostOfCredit] = useType("number", null);
  const [displayOnWebsite, setDisplayOnWebsite] = useType("boolean", false);
  const [activelyAdvertise, setActivelyAdvertise] = useType("boolean", false);
  const [transmissionType, setTransmissionType] = useType("string", "");
  const [fuelType, setFuelType] = useType("string", "");
  const [startDate, setStartDate] = useState(startDateInit);
  const [startDateUI, setStartDateUI] = useState(startDateInit);
  const [endDate, setEndDate] = useState(endDateInit);
  const [endDateUI, setEndDateUI] = useState(endDateInit);
  const [listDate, setListDate] = useState(listDateInit);
  const [listDateUI, setListDateUI] = useState(listDateInit);
  const [firstRegDate, setFirstRegDate] = useState(firstRegDateInit);
  const [firstRegDateUI, setFirstRegDateUI] = useState(firstRegDateInit);
  const [stockLocation, setStockLocation] = useType("string", "");
  const [condition, setCondition] = useType("string", "");
  const [sellingPrice, setSellingPrice] = useType("number", null);
  // const [standInValue, setStandInValue] = useType("number", null);
  const [mileage, setMileage] = useType("number", null);
  const [colour, setColour] = useType("string", "");
  const [retailPriceExclVAT, setRetailPriceExclVAT] = useType("number", null);
  const [retailPriceInclVAT, setRetailPriceInclVAT] = useType("number", null);
  const [displayedDiscount, setDisplayedDiscount] = useType("number", null);
  const [carType, setCarType] = useType("string", null);
  const [advertisedPrice, setAdvertisedPrice] = useType("number", null);
  const [notes, setNotes] = useType("string", "");
  const [localImages, setLocalImages] = useState([]);
  const [allImages, setAllImages] = useState([]);

  const [showMMCodeModal, setShowMMCodeModal] = useType("boolean", false);
  const [showMMCodeModalAuto, setShowMMCodeModalAuto] = useType("boolean", false);
  const [hasNoMMCodeResult, setHasNoMMCodeResult] = useType("boolean", false);
  const [showVinModal, setShowVinModal] = useType("boolean", false);
  const [prepopulateItem, setPrepopulateItem] = useType("object", {});
  const [includeInScheme, setIncludeInScheme] = useType("boolean", false);
  const [modelID, setModelID] = useType("string", null);
  const [mayProceed, setMayProceed] = useType("boolean", false);
  const [isSaving, setIsSaving] = useType("boolean", false);

  const [showDisplayOnWebsiteWarning, setShowDisplayOnWebsiteWarning] = useType(
    "boolean",
    false
  );
  const [showImagesGallery, setShowImagesGallery] = useType("boolean", false);
  const [showDealAuditHistory, setShowDealAuditHistory] = useType("boolean", false);

  const { NEW, DEMO, USED } = CONDITIONS;
  const newDemoUsed =
    dealCondition === NEW ? 1 : dealCondition === DEMO ? 2 : 3;
  const [addToast] = useToast();
  const [, dispatchDeals] = useDealsContext();
  const [, dispatchModalHistory] = useModalHistory();
  const popAll = () => {
    dispatchModalHistory({ type: MODAL_ACTIONS.POP_ALL });
    navigate("/");
  };

  const [fetchBrands, brands, isFetchingBrands] = useAsync(
    useCallback(() => api.getBrands(false), []),
    "",
    "Issue fetching brands."
  );
  const brandID = brands?.find((b) => b.brand === brand)?.brand_ID;
  const [fetchRanges, ranges, isFetchingRanges] = useAsync(
    useCallback(() => api.getRanges(brandID, 3), [brandID]),
    "",
    "Issue fetching ranges."
  );
  const rangeID = ranges?.find((r) => r.range === range)?.range_ID;
  const [fetchModels, models, isFetchingModels] = useAsync(
    useCallback(() => api.getModels(rangeID, 3), [rangeID]),
    "",
    "Issue fetching models."
  );

  const [fetchBackendImages, backendImages, isFetchingBackendImages] = useAsync(
    useCallback(() => api.getUsedDealImages(dealInfo?.used_Car_ID).then(images => {
      console.log("images", images);
      setAllImages(mapBackendImages(images));
      return images;
    }), [dealInfo]),
    "",
    "Issue fetching deal images."
  );
  const imageRef = useRef();

  const [fetchStockLocations, stockLocations, isFetchingStockLocations] =
    useAsync(
      useCallback(() => api.getStockLocations(), []),
      "",
      "Issue fetching stock locations.",
    );
  
  // Get auto trader info
  useEffect(() => {
    if(hasNoValue(carType)) return;

    setMayProceed(false);
    (async () => {
      const autoTraderListings = await api.getAutoTraderListings();
      console.log('autoTraderListings', autoTraderListings);
      if(!autoTraderListings || autoTraderListings?.length < 1) {
        setMayProceed(true);
        return;
      }

      // let valueToSearch = "";
      // if(carType === NEW) {
      //   valueToSearch = dealInfo?.manufacturer_ID;
      // } else {
      //   valueToSearch = dealInfo?.stock_No;
      // }
      if(!hasNoValue(dealInfo?.stock_No)) {
        const autoTraderListing = autoTraderListings.find(row => row?.stockNumber === dealInfo?.stock_No);
        console.log('autoTraderListing', autoTraderListing);
        if(hasNoValue(autoTraderListing)) {
          // send email
          setAutoTraderPriceInclVAT(0.00); 
          setAutoTraderLink("");
          return;
        } else {
          setAutoTraderPriceInclVAT(parseDecimal(autoTraderListing?.price?.replaceAll(",", "."))); 
          setAutoTraderLink(`https://www.autotrader.co.za/car-for-sale/${encodeURIComponent(autoTraderListing.make)}/${encodeURIComponent(autoTraderListing.model)}/${encodeURIComponent(autoTraderListing.variant)}/${encodeURIComponent(autoTraderListing.id)}`);
        }
      }
      setMayProceed(true);
    })();
  }, [carType, NEW, dealInfo?.manufacturer_ID, dealInfo?.stock_No, setAutoTraderLink, setAutoTraderPriceInclVAT]);

  useUpdateEffectOnce(() => {
    // Auto search for mmcode when pasted in if there is not retail price | transmission type | fuel type - Auto select's the one with status of active or shows popup if no active
    if(!hasNoValue(mmCode) && (hasNoValue(retailPriceInclVAT) || retailPriceInclVAT === 0 || hasNoValue(transmissionType) || hasNoValue(fuelType))) {
      setShowMMCodeModal(true);
      setShowMMCodeModalAuto(true);
    }
  }, !hasNoValue(mmCode));

  // Get brands
  useEffect(() => {
    fetchBrands(true);
  }, [fetchBrands]);

  // Get images
  useEffect(() => {
    if (!dealInfo?.used_Car_ID) return;
    fetchBackendImages(true);
  }, [dealInfo, fetchBackendImages]);

  // Get stock locations
  useEffect(() => {
    fetchStockLocations(true);
  }, [fetchStockLocations]);

  // Prepopulation based on brand range model result (Creating deal)
  useEffect(() => {
    if (hasNoValue(prepopulateItem)) {
      return;
    }

    (() => {
      setBrand(prepopulateItem?.brand || "");
      setRange(prepopulateItem?.range || "");
      setModel(prepopulateItem?.model || "");
      setModelID(prepopulateItem?.model_ID || "");
      setMMCode(prepopulateItem?.mmCode || "");
      setRetailPriceExclVAT(parseDecimal(prepopulateItem?.price_Ex_Vat));
      setRetailPriceInclVAT(parseDecimal(prepopulateItem?.price));
      setVin(prepopulateItem?.vinNumber || vin || "");
      setTransmissionType(prepopulateItem?.transmission || "");
      setFuelType(prepopulateItem?.fuel || "");
    })();
  }, [
    prepopulateItem,
    vin,
    setBrand,
    setRange,
    setModel,
    setModelID,
    setMMCode,
    setRetailPriceExclVAT,
    setRetailPriceInclVAT,
    setVin,
    setTransmissionType,
    setFuelType,
    USED,
  ]);

  // Prepopulation based on dealInfo result (Editing deal)
  useEffect(() => {
    if (hasNoValue(dealInfo)) {
      return;
    }

    (() => {
      // setReconCost(parseDecimal(dealInfo?.recon_Cost));
      // setReconTime(parseDecimal(dealInfo?.recon_Time));
      // setActiveDeal(dealInfo?.active_Deal);
      setDisplayOnWebsite(dealInfo?.display_On_Website);
      setActivelyAdvertise(dealInfo?.actively_Advertise);
      setMMCode(dealInfo?.mM_Code);
      setVin(dealInfo?.vin_Number);
      setBrand(dealInfo?.brand || "");
      setRange(dealInfo?.range || "");
      setModel(dealInfo?.model || "");
      setModelID(dealInfo?.model_ID || "");
      setSellingPrice(parseDecimal(dealInfo?.selling_Price));
      // setStandInValue(parseDecimal(dealInfo?.stand_In_Value));
      setMileage(parseDecimal(dealInfo?.mileage));
      setColour(dealInfo?.colour || "");
      setListDate(dealInfo?.list_Date || listDateInit);
      const [listDateInitUI] = getDate(dealInfo?.list_Date || listDateInit);
      setListDateUI(listDateInitUI || "");
      setFirstRegDate(dealInfo?.first_Reg_Date || firstRegDateInit);
      const [firstRegDateInitUI] = getDate(dealInfo?.first_Reg_Date || firstRegDateInit);
      setFirstRegDateUI(firstRegDateInitUI || "");
      setStockLocation(dealInfo?.stock_Location || "");
      setCondition(dealInfo?.condition || "");
      setTransmissionType(dealInfo?.transmission || "");
      setFuelType(dealInfo?.fuel_Type || "");
      setRetailPriceExclVAT(parseDecimal(dealInfo?.retail_Price_Excl_VAT));
      setRetailPriceInclVAT(parseDecimal(dealInfo?.retail_Price_Incl_VAT));
      setCarType(dealInfo?.carType || "");
      setDisplayedDiscount(parseDecimal(dealInfo?.displayedDiscount));
      setDealAssistance(parseDecimal(dealInfo?.deal_Assistance));
      setStartDate(dealInfo?.start_Date || "");
      setEndDate(dealInfo?.end_Date || "");
      setAdvertisedPrice(parseDecimal(dealInfo?.advertised_Price));
      setRatePercent(parseDecimal(dealInfo?.rate_Percent));
      setRateType(dealInfo?.rate_Type || "");
      setDeposit(parseDecimal(dealInfo?.deposit));
      setPeriod(parseDecimal(dealInfo?.period));
      setBalloonPercent(parseDecimal(dealInfo?.balloon_Percent || 35));
      setBalloonAmount(parseDecimal(dealInfo?.balloon_Amount));
      setTotalCostOfCredit(parseDecimal(dealInfo?.total_Cost_Of_Credit));
      setNotes(dealInfo?.notes || "");
      setReportedToOEM(dealInfo?.reported_To_OEM || false);
      setPhotosTaken(dealInfo?.photos_Taken || false);
      setPhotosEdited(dealInfo?.photos_Edited || false);
      setUnclaimedRebates(dealInfo?.unclaimed_Rebates || 0);
      setDemoAvailableToSellDate(dealInfo?.demo_Available_To_Sell_Date || "");
      const [demoAvailableToSellDateUI] = getDate(dealInfo?.demo_Available_To_Sell_Date || "");
      setDemoAvailableToSellDateUI(demoAvailableToSellDateUI || "");
      setReconWorkDone(dealInfo?.recon_Work_Done || false);
      setCheck101Done(dealInfo?.check_101_Done || false);
      setDoNotSell(dealInfo?.do_Not_Sell || false);
      setDoNotSellReason(dealInfo?.do_Not_Sell_Reason || "");
      setDoNotSellEndDate(dealInfo?.do_Not_Sell_End_Date || "");
      const [doNotSellEndDateUI] = getDate(dealInfo?.do_Not_Sell_End_Date || "");
      setDoNotSellEndDateUI(doNotSellEndDateUI || "");
      setNotArrivedYet(dealInfo?.not_Arrived_Yet || false);
    })();
  }, [
    dealInfo,
    listDateInit,
    // setReconCost,
    // setReconTime,
    // setActiveDeal,
    setDisplayOnWebsite,
    setActivelyAdvertise,
    setMMCode,
    setVin,
    setBrand,
    setRange,
    setModel,
    setModelID,
    setSellingPrice,
    // setStandInValue,
    setMileage,
    setColour,
    setListDate,
    setFirstRegDate,
    firstRegDateInit,
    setStockLocation,
    setCondition,
    setTransmissionType,
    setFuelType,
    setRetailPriceExclVAT,
    setRetailPriceInclVAT,
    setDisplayedDiscount,
    setCarType,
    setDealAssistance,
    setStartDate,
    setEndDate,
    setAdvertisedPrice,
    setRatePercent,
    setRateType,
    setDeposit,
    setPeriod,
    setBalloonPercent,
    setBalloonAmount,
    setTotalCostOfCredit,
    setNotes,
    dealCondition,
    setPhotosEdited, 
    setPhotosTaken, 
    setReportedToOEM,
    setUnclaimedRebates,
    setDemoAvailableToSellDate,
    setReconWorkDone,
    setCheck101Done,
    setDoNotSell,
    setDoNotSellReason,
    setDoNotSellEndDate,
    setNotArrivedYet,
  ]);

  useEffect(() => {
    (async () => {
      if(!localImages || localImages.length === 0) return;

      const images = await mapLocalImages(localImages);
      setAllImages((prev) => {
        console.log('prev', prev);
        console.log('images', images);
        return [...prev, ...images];
      });
    })();
  }, [localImages, setAllImages]);

  // Handle required fields for List Price
  const requiredFieldsListPrice = useMemo(
    () => ({
      mmCode: {
        value: mmCode,
        validate: () => (!mmCode ? "Please fill out this field" : ""),
      },
      vin: {
        value: vin,
        validate: () => validateVinNo(vin),
      },
      brand: {
        value: brand,
        validate: (option) =>
          !brand && !option ? "Please fill out this field" : "",
      },
      range: {
        value: range,
        validate: (option) =>
          !range && !option ? "Please fill out this field" : "",
      },
      model: {
        value: model,
        validate: (option) =>
          !model && !option ? "Please fill out this field" : "",
      },
      // sellingPrice: {
      //   value: sellingPrice,
      //   validate: (option) =>
      //     !sellingPrice && !option ? "Please fill out this field" : "",
      // },
      // standInValue: {
      //   value: standInValue,
      //   validate: (option) =>
      //     !standInValue && !option ? "Please fill out this field" : "",
      // },
      mileage: {
        value: mileage,
        validate: (option) =>
          !mileage && !option ? "Please fill out this field" : "",
      },
      colour: {
        value: colour,
        validate: (option) =>
          !colour && !option ? "Please fill out this field" : "",
      },
      // listDate: {
      //   value: listDate,
      //   validate: (option) =>
      //     !listDate && !option
      //       ? "Please fill out this field"
      //       : "",
      // },
      firstRegDate: {
        value: firstRegDate,
        validate: (option) =>
          !firstRegDate && !option
            ? "Please fill out this field"
            : "",
      },
      stockLocation: {
        value: stockLocation,
        validate: (option) =>
          !stockLocation && !option ? "Please fill out this field" : "",
      },
      // condition: {
      //   value: condition,
      //   validate: (option) =>
      //     !condition && !option ? "Please fill out this field" : "",
      // },
      transmission: {
        value: transmissionType,
        validate: (option) =>
          !transmissionType && !option ? "Please fill out this field" : "",
      },
      fuelType: {
        value: fuelType,
        validate: (option) =>
          !fuelType && !option ? "Please fill out this field" : "",
      },
      carType: {
        value: carType,
        validate: (option) =>
          !carType && !option ? "Please fill out this field" : "",
      },
      retailPriceExclVAT: {
        value: retailPriceExclVAT,
        validate: (option) => {
          if (!retailPriceExclVAT && !hasNoMMCodeResult && !option)
            return "Please fill out this field";
          return "";
        },
      },
      retailPriceInclVAT: {
        value: retailPriceInclVAT,
        validate: (option) => {
          if (!retailPriceInclVAT && !hasNoMMCodeResult && !option)
            return "Please fill out this field";
          return "";
        },
      },
      displayedDiscount: {
        value: displayedDiscount,
        validate: (option) => {
          if (isNaN(displayedDiscount) && isNaN(option))
            return "Please fill out this field";
          return "";
        },
      },

      unclaimedRebates: {
        value: unclaimedRebates,
        validate: (option) => {
          if (isNaN(unclaimedRebates) && isNaN(option))
            return "Please fill out this field";
          return "";
        },
      },
      // autoTraderPriceInclVAT: {
      //   value: autoTraderPriceInclVAT,
      //   validate: (option) => {
      //     if (isNaN(autoTraderPriceInclVAT) && isNaN(option))
      //       return "Please fill out this field";
      //     return "";
      //   },
      // },
      demoAvailableToSellDate: {
        value: demoAvailableToSellDate,
        validate: (option) =>
          (!demoAvailableToSellDate && !option) && carType.toLowerCase() === CONDITIONS.DEMO.toLowerCase()
            ? "Please fill out this field"
            : "",
      },
      doNotSellReason: {
        value: doNotSellReason,
        validate: (option) =>
          (!doNotSellReason && !option) && doNotSell === true
            ? "Please fill out this field"
            : "",
      },
      doNotSellEndDate: {
        value: doNotSellEndDate,
        validate: (option) =>
          (!doNotSellEndDate && !option) && doNotSell === true
            ? "Please fill out this field"
            : "",
      },
      displayOnWebsite: {
        value: displayOnWebsite,
        validate: (option) =>
          displayOnWebsite === true && parseDecimal(Math.floor(sellingPrice - displayedDiscount)) < 45000 
            ? "Advertised selling price too low"
            : "",
      },
    }),
    [
      mmCode,
      vin,
      brand,
      range,
      model,
      colour,
      // condition,
      firstRegDate,
      fuelType,
      carType,
      // listDate,
      mileage,
      retailPriceExclVAT,
      retailPriceInclVAT,
      displayedDiscount,
      sellingPrice,
      // standInValue,
      stockLocation,
      transmissionType,
      unclaimedRebates,
      demoAvailableToSellDate,
      doNotSell,
      doNotSellEndDate,
      doNotSellReason,
      hasNoMMCodeResult,
      displayOnWebsite,
    ]
  );

  // Handle required fields for Scheme
  const requiredFieldsScheme = useMemo(
    () => ({
      ...requiredFieldsListPrice,

      startDate: {
        value: startDate,
        validate: (option) =>
          !startDate && !option
            ? "Please fill out this field"
            : "",
      },
      endDate: {
        value: endDate,
        validate: (option) =>
          !endDate && !option
            ? "Please fill out this field"
            : "",
      },
      advertisedPrice: {
        value: advertisedPrice,
        validate: (option) => {
          if (!advertisedPrice && !option) return "Please fill out this field";
          return "";
        },
      },
      ratePercent: {
        value: ratePercent,
        validate: (option) => {
          if (!ratePercent && !option) return "Please fill out this field";
          return "";
        },
      },
      rateType: {
        value: rateType,
        validate: (option) =>
          !rateType && !option ? "Please fill out this field" : "",
      },
      deposit: {
        value: deposit,
        validate: (option) => {
          if (isNaN(deposit) && isNaN(option)) return "Please fill out this field";
          return "";
        },
      },
      period: {
        value: period,
        validate: (option) => {
          if (!period && !option) return "Please fill out this field";
          return "";
        },
      },
      balloonPercent: {
        value: balloonPercent,
        validate: (option) => {
          if (isNaN(balloonPercent) && isNaN(option)) return "Please fill out this field";
          return "";
        },
      },
      balloonAmount: {
        value: balloonAmount,
        validate: (option) => {
          if (isNaN(balloonAmount) && isNaN(option)) return "Please fill out this field";
          return "";
        },
      },
    }),
    [
      startDate,
      endDate,
      advertisedPrice,
      balloonAmount,
      balloonPercent,
      deposit,
      period,
      ratePercent,
      rateType,
      requiredFieldsListPrice,
    ]
  );

  // Handle validations
  useEffect(() => {
    if (includeInScheme) {
      if (
        Object.values(requiredFieldsScheme).filter(
          (field) => field.validate().length > 0
        ).length > 0
      ) {
        setMayProceed(false);
      } else {
        setMayProceed(true);
      }
    } else {
      if (
        Object.values(requiredFieldsListPrice).filter(
          (field) => field.validate().length > 0
        ).length > 0
      ) {
        setMayProceed(false);
      } else {
        setMayProceed(true);
      }
    }
  }, [
    includeInScheme,
    requiredFieldsScheme,
    requiredFieldsListPrice,
    setMayProceed,
  ]);

  // Handle TCOC
  useEffect(() => {
    const tcoc = calculateTotalCostOfCredit(
      advertisedPrice,
      ratePercent,
      deposit,
      balloonPercent,
      period
    );
    setTotalCostOfCredit(tcoc);
  }, [
    advertisedPrice,
    ratePercent,
    deposit,
    balloonPercent,
    period,
    setTotalCostOfCredit,
  ]);

  // OnSubmit: Show error popup if display on website === true && no images are uploaded
  // const handleDisplayOnWebsiteWarning = () => {
  //   if (displayOnWebsite && uploadedImages?.length === 0) {
  //     setShowDisplayOnWebsiteWarning(true);
  //   } else {
  //     if (scope === SCOPES.CREATE) handleDuplicate();
  //     else if (scope === SCOPES.EDIT) handleSubmit();
  //   }
  // };

  // Handle checking if deals already exist
  // const handleDuplicate = async () => {
  //   console.log("handleDuplicate");
  //   const cdpResp = await api.getCarDataPubUsedDealByModelID(modelID);
  //   const pldResp = await api.getPreloadedUsedDealByUsedCarID(
  //     cdpResp?.preloadedDeal_ID
  //   );

  //   const isDuplicateCDPDeal =
  //     cdpResp?.message === API_RESPONSE.CARDATAPUBDEAL_FOUND;

  //   const isDuplicatePLDDeal =
  //     pldResp?.message === API_RESPONSE.PRELOADEDDEAL_FOUND;
  //   setIsPLDDuplicateDeal(isDuplicatePLDDeal);

  //   console.log("isDuplicateCDPDeal", isDuplicateCDPDeal);
  //   console.log("isDuplicatePLDDeal", isDuplicatePLDDeal);

  //   if (isDuplicateCDPDeal) {
  //     console.log("show CDP duplicate");
  //     setCDPDealFoundID(cdpResp?.preloadedDeal_ID);
  //     setShowDuplicateCDPDealFound(true);
  //   } else if (isDuplicatePLDDeal && includeInScheme) {
  //     console.log("show PLD duplicate");

  //     setShowDuplicatePLDDealFound(true);
  //   }

  //   if (isDuplicateCDPDeal === false && isDuplicatePLDDeal === false) {
  //     handleSubmit();
  //   }
  // };

  // Handle inserting used deal (CDP)
  const handleCDPDealInsert = async (data) => {
    console.log("scope", scope);
    console.log("CarDataPub_UsedCar input", data);

    const cdpResp = await api.insertCDPUsedDeal(data);
    console.log("CarDataPub_UsedCar output", cdpResp);

    if (cdpResp?.success) {
    } else {
      setIsSaving(false);
      addToast(TOAST_TYPES.ERROR, "Error creating List Price deal.");
    }
    return cdpResp?.id;
  };

  // Handle updating used deal (CDP)
  const handleCDPDealUpdate = async (data) => {
    console.log("scope", scope);
    console.log("CarDataPub_UsedCar input", data);
    const cdpResp = await api.updateCDPUsedDeal(data);
    console.log("CarDataPub_UsedCar output", cdpResp);

    if (cdpResp?.success) {
    } else {
      setIsSaving(false);
      addToast(TOAST_TYPES.ERROR, "Error updating List Price deal.");
    }
    return cdpResp?.id;
  };

  // Handle inserting used deal (PLD)
  const handlePLDDealInsert = async (data) => {
    console.log("PreloadedDeal_UsedCar input", data);
    const pldResp = await api.insertPLDUsedDeal(data);
    console.log("PreloadedDeal_UsedCar output", pldResp);

    if (pldResp?.success) {
    } else {
      setIsSaving(false);
      addToast(TOAST_TYPES.ERROR, "Error creating Scheme deal.");
    }
    return pldResp?.id;
  };

  // Handle updating used deal (PLD)
  const handlePLDDealUpdate = async (data) => {
    console.log("PreloadedDeal_UsedCar input", data);
    const pldResp = await api.updatePLDUsedDeal(data);
    console.log("PreloadedDeal_UsedCar output", pldResp);

    if (pldResp?.success) {
    } else {
      setIsSaving(false);
      addToast(TOAST_TYPES.ERROR, "Error updating Scheme deal.");
    }
    return pldResp?.id;
  };

  const handleUpsertUsedImage = async () => {
    const validImages = allImages?.map((img, index) => ({...img, imageNumber: ++index}))?.filter(img => (img.isLocal || img.modified) && !img.tooLarge);
    console.log("🚀 ~ handleSubmit ~ validImages:", validImages);

    if (validImages?.length > 0) {
      // Batch images into groups below 5MB each
      const imageBatches = batchImagesBySize(validImages, 5);
      console.log("🚀 ~ handleSubmit ~ imageBatches:", imageBatches)
      console.log(`Batching ${validImages.length} images into ${imageBatches.length} batch(es)`);

      let hasError = false;
      for (let i = 0; i < imageBatches.length; i++) {
        const imageBatch = imageBatches[i];
        const dealImagesInput = imageBatch.map((image) => {
          const imageInput = {
            ImageID: image.imageID,
            UsedCarID: dealInfo?.used_Car_ID,
            ModelID: modelID,
            ByteArray: [...new Uint8Array(image?.byteArray)],
            FileName: image?.fileName,
            FileExt: image?.fileExt,
            ImageNumber: image?.imageNumber,
            Description: `Used ${colour} ${model} Image`,
          };

          return imageInput;
        });
        console.log(`Uploading batch ${i + 1}/${imageBatches.length}:`, dealImagesInput);
        
        const imageResp = await api.upsertUsedDealImages(dealImagesInput);
        console.log(`Batch ${i + 1} response:`, imageResp);
        if (imageResp !== "Success") {
          hasError = true;
          addToast(TOAST_TYPES.ERROR, `Error uploading image batch ${i + 1}.`);
        }
      }

      if (!hasError) {
        addToast(TOAST_TYPES.SUCCESS, "All images uploaded successfully.");
      }
    }
  }

  const handleInsertUsedDeal = async (usedCarID) => {
    const validImages = allImages?.map((img, index) => ({...img, imageNumber: ++index}))?.filter(img => (img.isLocal || img.modified) && !img.tooLarge);
    console.log("🚀 ~ handleSubmit ~ validImages:", validImages);

    if (validImages?.length > 0) {
      // Batch images into groups below 5MB each
      const imageBatches = batchImagesBySize(validImages, 5);
      console.log(`Batching ${validImages.length} images into ${imageBatches.length} batch(es)`);

      let hasError = false;
      for (let i = 0; i < imageBatches.length; i++) {
        const imageBatch = imageBatches[i];
        const dealImagesInput = imageBatch.map((image) => {
          const imageInput = {
            UsedCarID: usedCarID,
            ModelID: modelID,
            ByteArray: [...new Uint8Array(image?.byteArray)],
            FileName: image?.fileName,
            FileExt: image?.fileExt,
            ImageNumber: image?.imageNumber,
            Description: `Used ${colour} ${model} Image`,
          };

          return imageInput;
        });
        console.log(`Uploading batch ${i + 1}/${imageBatches.length}:`, dealImagesInput);

        const imageResp = await api.insertUsedDealImages(dealImagesInput);
        console.log(`Batch ${i + 1} response:`, imageResp);
        if (imageResp !== "Success") {
          hasError = true;
          addToast(TOAST_TYPES.ERROR, `Error uploading image batch ${i + 1}.`);
        }
      }

      if (!hasError) {
        addToast(TOAST_TYPES.SUCCESS, "All images uploaded successfully.");
      }
    }
  }

  console.log('listDate', listDate);

  // Handle updating/inserting deals (CDP, PLD)
  const handleSubmit = async () => {
    console.log("handleSubmit");
    setIsSaving(true);
    const date = new Date(dealInfo?.first_Reg_Date);
    let dateDD = date.getDate();
    let dateMM = date.getMonth() + 1;
    let dateYYYY = date.getFullYear();
    if (dateDD < 10) dateDD = "0" + dateDD;
    if (dateMM < 10) dateMM = "0" + dateMM;
    // const firstRegDate = `${dateYYYY}-${dateMM}-${dateDD}`;

    const carDataPubData = {
      UsedCarID: dealInfo?.used_Car_ID,
      UserID: parseInt(get(USER_KEY)?.userID),

      // ReconCost: parseDecimal(dealInfo?.recon_Cost),
      // ReconTime: parseDecimal(reconTime),
      ActiveDeal: displayOnWebsite === true ? 1 : 0,
      DisplayOnWebsite: displayOnWebsite === true ? 1 : 0,
      ActivelyAdvertise: activelyAdvertise === true ? 1 : 0,
      MMCode: mmCode,
      VinNumber: vin,
      Brand: brand,
      Range: range,
      Model: model,
      ModelID: modelID,
      SellingPrice: parseDecimal(sellingPrice),
      // StandInValue: parseDecimal(standInValue),
      DisplayedDiscount: parseDecimal(displayedDiscount),
      CarType: carType,
      Mileage: parseDecimal(mileage),
      Colour: colour,
      ListDate: listDate,
      FirstRegDate: firstRegDate,
      StockLocation: stockLocation,
      Condition: condition,
      Transmission: transmissionType?.toLowerCase(),
      FuelType: fuelType?.toLowerCase(),
      DealAssistance: parseDecimal(dealAssistance),
      Notes: notes,
      //UploadedPictures: "images",
      SearchBarcode: `${brand}_${range}_${model}_${sellingPrice}_${mmCode}_${notes}`,

      ReportedToOEM: reportedToOEM === true ? 1 : 0,
      PhotosTaken: photosTaken === true ? 1 : 0,
      PhotosEdited: photosEdited === true ? 1 : 0,
      ReconWorkDone: reconWorkDone === true ? 1 : 0,
      UnclaimedRebates: parseDecimal(unclaimedRebates),
      AutoTraderPriceInclVAT: parseDecimal(autoTraderPriceInclVAT),
      DoNotSell: doNotSell === true ? 1 : 0,
      DoNotSellEndDate: doNotSellEndDate,
      DoNotSellReason: doNotSellReason,
      NotArrivedYet: notArrivedYet === true ? 1 : 0,
    };

    if(carType.toLowerCase() === DEMO.toLowerCase()) {
      carDataPubData.DemoAvailableToSellDate = demoAvailableToSellDate;
    }
    if(carType.toLowerCase() === USED.toLowerCase()) {
      carDataPubData.Check101Done = check101Done === true ? 1 : 0;
    }
    
    const preloadedDealData = {
      UsedCarID: "",
      StartDate: startDate,
      EndDate: endDate,
      AdvertisedPrice: parseDecimal(advertisedPrice),
      RatePercent: parseDecimal(ratePercent),
      RateType: rateType,
      Deposit: parseDecimal(deposit),
      Period: parseInt(period) || 0,
      BalloonPercent: parseDecimal(balloonPercent),
      BalloonAmount: parseDecimal(balloonAmount),
      TotalCostOfCredit: parseDecimal(totalCostOfCredit),
    };

    // Handle create
    if (scope === SCOPES.CREATE) {
      // Handle CDP
      let usedCarID = "";

      usedCarID = await handleCDPDealInsert(carDataPubData);
      console.log("usedCarID", usedCarID);

      await handleInsertUsedDeal(usedCarID);

      // Handle PLD
      if (includeInScheme) {
        handlePLDDealInsert({
          ...preloadedDealData,
          UsedCarID: usedCarID,
        });
      }
    } else if (scope === SCOPES.EDIT) {
      // Handle edit
      await handleCDPDealUpdate(carDataPubData);
      
      const cdpDifferences = (await getCDPDealDifferences()).map(d => ({...d, dealType: "ListPrice"}));
      await insertDealAudit(cdpDifferences);

      const sellingPriceDiff = cdpDifferences.find((d) => d.key === 'SellingPrice');

      if (sellingPriceDiff) {
        // Used / Demo / New-in-stock cars live in CarDataPub_UsedCars and are
        // synced by the full-document Elastic Search pipeline, not the
        // price-only fast path. Flagging RequiresAnUpdate is enough.
        const elasticSearchPayload = {
          UsedCarID: dealInfo?.used_Car_ID, 
        }
        const elasticSearchUpdateResp = await api.updateElasticSearchPrice(elasticSearchPayload);
        console.log("elasticSearchUpdateResp output", elasticSearchUpdateResp);

        if (elasticSearchUpdateResp?.success) {
        } else {
          setIsSaving(false);
          addToast(TOAST_TYPES.ERROR, "Error updating Elastic Search price.");
        }
      }

      const vehicleStockToUpdateResp = await api.runCOQLQuery(
        `SELECT id FROM Vehicle_Stock WHERE (Used_Car_ID = '${carDataPubData.UsedCarID}') LIMIT 0, 1`
      );

      if (vehicleStockToUpdateResp?.data?.length > 0) {
        const vehicleStockToUpdate = vehicleStockToUpdateResp?.data?.[0];

        const onCarterWebsite = (displayOnWebsite);
        const priceMatching = carDataPubData.SellingPrice === autoTraderPriceInclVAT;
        const webstiesUpToDate = onCarterWebsite && autoTraderLink?.length > 0;

        const vehicleStockPayload = {
          id: vehicleStockToUpdate.id,
          Reported_on_Autoline: reportedToOEM,
          Unclaimed_Rebates_Incl_VAT: unclaimedRebates,
          OEM_Recommended_Retail_Price_Incl_VAT: parseDecimal(retailPriceInclVAT),
          Carter_Retail_Price_Incl_VAT: parseDecimal(Math.floor(sellingPrice - displayedDiscount)),
          Effective_Discount: parseDecimal(displayedDiscount),
          Accessories: notes,
          Photos_Taken: photosTaken,
          Photos_Edited: photosEdited,
          // Link_To_Car_On_AutoTrader: autoTraderLink,
          // Price_On_AutoTrader_Incl_VAT: parseDecimal(autoTraderPriceInclVAT),
          Recon_Work_Done: reconWorkDone,
          Do_Not_Sell: doNotSell,
          Not_Arrived_Yet: notArrivedYet,
          On_Carter_Website: onCarterWebsite,
          Carter_Website_Link: onCarterWebsite ? carterWebsiteLink : "",
          Price_Matching: priceMatching,
          Websites_Up_To_Date: webstiesUpToDate,
          Price_On_AutoTrader_Incl_VAT: parseDecimal(autoTraderPriceInclVAT),
          Link_To_Car_On_AutoTrader: autoTraderLink,
        };

        if(vehicleStockPayload.Do_Not_Sell) {
          vehicleStockPayload.Do_Not_Sell_Reason = doNotSellReason;
          vehicleStockPayload.Do_Not_Sell_End_Date = getDate(doNotSellEndDate)[0];
        } else {
          vehicleStockPayload.Do_Not_Sell_Reason = "";
          vehicleStockPayload.Do_Not_Sell_End_Date = null;
        }

        if (carType.toLowerCase() === DEMO.toLowerCase()) {
          vehicleStockPayload.Date_Demo_Available_To_Sell = getDate(demoAvailableToSellDate)[0];
        }
        if (carType.toLowerCase() === USED.toLowerCase()) {
          vehicleStockPayload.Is_101_Check_Done = check101Done;
        }

        console.log("vehicleStockPayload: ", vehicleStockPayload);
      
        await api.updateZohoRecord({
          Module: "Vehicle_Stock",
          Json: JSON.stringify(vehicleStockPayload),
        });
      }

      await handleUpsertUsedImage();

      if (includeInScheme) {
        let data = {
          ...preloadedDealData,
          UsedCarID: dealInfo?.used_Car_ID,
        };
        handlePLDDealUpdate(data);
        const pldDifferences = await getPLDDealDifferences().map(d => ({...d, dealType: "InstallmentPrice"}));
        await insertDealAudit(pldDifferences);
      }
    }

    const isCreatingDeal = scope === SCOPES.CREATE;
    const isDisplayOnWebsiteChanged = dealInfo?.display_On_Website !== displayOnWebsite;
    // Send email
    if(isCreatingDeal || isDisplayOnWebsiteChanged) {
      await sendSRMEmail(isCreatingDeal, isDisplayOnWebsiteChanged);
    }

    dispatchDeals({
      type: DEAL_ACTIONS.REFETCH_DEALS,
      payload: true,
    });
    popAll();
  };

  async function test() {
    
  }

  async function getCDPDealDifferences() {
    /*
{
    "used_Car_ID": 82,
    "recon_Cost": 0,
    "recon_Time": 0,
    "active_Deal": true,
    "display_On_Website": false,
    "actively_Advertise": false,
    "mM_Code": "54065276",
    "vin_Number": "VF1RZG00862318899",
    "brand": "Renault",
    "range": "Koleos",
    "model": "2.5 4x4 Dynamique auto",
    "model_ID": "54065276",
    "selling_Price": 439900,
    "stand_In_Value": 36086957,
    "mileage": 11250,
    "colour": "Silver",
    "list_Date": "09/26/2019 00:00:00",
    "first_Reg_Date": "06/13/2019 00:00:00",
    "stock_Location": "Nation wide",
    "condition": "Excellent",
    "transmission": "automatic",
    "fuel_Type": "petrol",
    "retail_Price_Excl_VAT": 304260.87,
    "retail_Price_Incl_VAT": 349900,
    "deal_Assistance": 0,
    "search_Barcode": "Renault_Koleos_2.5 4x4 Dynamique auto_439900_54065276_",
    "start_Date": null,
    "end_Date": null,
    "advertised_Price": 0,
    "rate_Percent": 0,
    "rate_Type": null,
    "deposit": 0,
    "period": 0,
    "balloon_Percent": 0,
    "balloon_Amount": 0,
    "total_Cost_Of_Credit": 0,
    "notes": ""
}
    */
    const oldData = {
      // UserID: parseInt(get(USER_KEY)?.userID),
      // ReconCost: dealInfo?.recon_Cost,
      // ReconTime: dealInfo?.recon_Time,
      // toString() is used here to keep the values the same for the difference check below
      // ActiveDeal: dealInfo?.active_Deal?.toString(),
      DisplayOnWebsite: dealInfo?.display_On_Website?.toString(),
      ActivelyAdvertise: dealInfo?.actively_Advertise?.toString(),
      MMCode: dealInfo?.mM_Code ,
      VinNumber: dealInfo?.vin_Number ,
      Brand: dealInfo?.brand ,
      Range: dealInfo?.range ,
      Model: dealInfo?.model ,
      ModelID: dealInfo?.model_ID ,
      SellingPrice: dealInfo?.selling_Price ,
      // StandInValue: dealInfo?.stand_In_Value ,
      DisplayedDiscount: dealInfo?.displayedDiscount ,
      CarType: dealInfo?.carType ,
      Mileage: dealInfo?.mileage ,
      Colour: dealInfo?.colour ,
      ListDate: dealInfo?.list_Date ,
      FirstRegDate: dealInfo?.first_Reg_Date ,
      StockLocation: dealInfo?.stock_Location ,
      Condition: dealInfo?.condition ,
      Transmission: dealInfo?.transmission ,
      FuelType: dealInfo?.fuel_Type ,
      DealAssistance: dealInfo?.deal_Assistance ,
      Notes: dealInfo?.notes ,
      // SearchBarcode: dealInfo?.search_Barcode ,

      ReportedToOEM: dealInfo?.reported_To_OEM,
      PhotosTaken: dealInfo?.photos_Taken,
      PhotosEdited: dealInfo?.photos_Edited,
      Check101Done: dealInfo?.check_101_Done,
      ReconWorkDone: dealInfo?.recon_Work_Done,
      UnclaimedRebates: dealInfo?.unclaimed_Rebates,
      // AutoTraderPriceInclVAT: dealInfo?.auto_Trader_Price_Incl_VAT,
      DemoAvailableToSellDate: dealInfo?.demo_Available_To_Sell_Date,
      DoNotSell:              dealInfo?.do_Not_Sell?.toString(),
      DoNotSellEndDate:       dealInfo?.do_Not_Sell_End_Date,
      DoNotSellReason:        dealInfo?.do_Not_Sell_Reason,
      NotArrivedYet:          dealInfo?.not_Arrived_Yet?.toString(),
    };

    const newData = {
      // UserID: parseInt(get(USER_KEY)?.userID),
      // ReconCost: parseDecimal(reconCost),
      // ReconTime: parseDecimal(reconTime),
      // ActiveDeal: activeDeal?.toString(),
      DisplayOnWebsite: displayOnWebsite?.toString(),
      ActivelyAdvertise: activelyAdvertise?.toString(),
      MMCode: mmCode,
      VinNumber: vin,
      Brand: brand,
      Range: range,
      Model: model,
      ModelID: modelID,
      SellingPrice: parseDecimal(sellingPrice),
      // StandInValue: parseDecimal(standInValue),
      DisplayedDiscount: parseDecimal(displayedDiscount) ,
      CarType: carType ,
      Mileage: parseDecimal(mileage),
      Colour: colour,
      ListDate: listDate,
      FirstRegDate: firstRegDate,
      StockLocation: stockLocation,
      Condition: condition,
      Transmission: transmissionType,
      FuelType: fuelType,
      DealAssistance: parseDecimal(dealAssistance),
      Notes: notes,
      // SearchBarcode: `${brand}_${range}_${model}_${sellingPrice}_${mmCode}_${notes}`,

      ReportedToOEM: reportedToOEM,
      PhotosTaken: photosTaken,
      PhotosEdited: photosEdited,
      Check101Done: check101Done,
      ReconWorkDone: reconWorkDone,
      UnclaimedRebates: unclaimedRebates,
      // AutoTraderPriceInclVAT: autoTraderPriceInclVAT,
      DemoAvailableToSellDate: demoAvailableToSellDate,
      DoNotSell:              doNotSell?.toString(),
      DoNotSellEndDate:       doNotSellEndDate,
      DoNotSellReason:        doNotSellReason,
      NotArrivedYet:          notArrivedYet?.toString(),
    };

    if (!hasNoValue(dealInfo)) {
      console.log('dealInfo', dealInfo);
      console.log('oldData', oldData);
      console.log('newData', newData);
      const differences = getObjectDiff(oldData, newData);
      console.log('differences', differences);
      
      return differences;
    } else return [];
  }

  async function getPLDDealDifferences() {
    /*
{
    "used_Car_ID": 82,
    "recon_Cost": 0,
    "recon_Time": 0,
    "active_Deal": true,
    "display_On_Website": false,
    "actively_Advertise": false,
    "mM_Code": "54065276",
    "vin_Number": "VF1RZG00862318899",
    "brand": "Renault",
    "range": "Koleos",
    "model": "2.5 4x4 Dynamique auto",
    "model_ID": "54065276",
    "selling_Price": 439900,
    "stand_In_Value": 36086957,
    "mileage": 11250,
    "colour": "Silver",
    "list_Date": "09/26/2019 00:00:00",
    "first_Reg_Date": "06/13/2019 00:00:00",
    "stock_Location": "Nation wide",
    "condition": "Excellent",
    "transmission": "automatic",
    "fuel_Type": "petrol",
    "retail_Price_Excl_VAT": 304260.87,
    "retail_Price_Incl_VAT": 349900,
    "deal_Assistance": 0,
    "search_Barcode": "Renault_Koleos_2.5 4x4 Dynamique auto_439900_54065276_",
    "start_Date": null,
    "end_Date": null,
    "advertised_Price": 0,
    "rate_Percent": 0,
    "rate_Type": null,
    "deposit": 0,
    "period": 0,
    "balloon_Percent": 0,
    "balloon_Amount": 0,
    "total_Cost_Of_Credit": 0,
    "notes": ""
}
    */
    const oldData = {
      StartDate: dealInfo?.start_Date ,
      EndDate: dealInfo?.end_Date ,
      AdvertisedPrice: dealInfo?.advertised_Price ,
      RatePercent: dealInfo?.rate_Percent ,
      RateType: dealInfo?.rate_Type ,
      Deposit: dealInfo?.deposit ,
      Period: dealInfo?.period ,
      BalloonPercent: dealInfo?.balloon_Percent ,
      BalloonAmount: dealInfo?.balloon_Amount ,
      TotalCostOfCredit: dealInfo?.total_Cost_Of_Credit ,
    };

    const newData = {
      StartDate: startDate,
      EndDate: endDate,
      AdvertisedPrice: parseDecimal(advertisedPrice),
      RatePercent: parseDecimal(ratePercent),
      RateType: rateType,
      Deposit: parseDecimal(deposit),
      Period: parseInt(period) || 0,
      BalloonPercent: parseDecimal(balloonPercent),
      BalloonAmount: parseDecimal(balloonAmount),
      TotalCostOfCredit: parseDecimal(totalCostOfCredit),
    };

    if (!hasNoValue(dealInfo)) {
      console.log('dealInfo', dealInfo);
      console.log('oldData', oldData);
      console.log('newData', newData);
      const differences = getObjectDiff(oldData, newData);
      console.log('differences', differences);
      
      return differences;
    }
  }

  // async function getVehicleStockDifferences() {
  //   const oldData = {
  //     ReportedToOEM:          dealInfo?.reported_To_OEM,
  //     UnclaimedRebates:       dealInfo?.unclaimed_Rebates,
  //     RetailPriceInclVAT:     dealInfo?.retail_Price_Incl_VAT,
  //     SellingPrice:           dealInfo?.selling_Price,
  //     DisplayedDiscount:      dealInfo?.displayedDiscount,
  //     Notes:                  dealInfo?.notes,
  //     PhotosTaken:            dealInfo?.photos_Taken,
  //     PhotosEdited:           dealInfo?.photos_Edited,
  //     // AutoTraderLink:         dealInfo?.auto_Trader_Link,
  //     // AutoTraderPriceInclVAT: dealInfo?.auto_Trader_Price_Incl_VAT,
  //     DemoAvailableToSellDate: dealInfo?.demo_Available_To_Sell_Date,
  //     Check101Done:           dealInfo?.check_101_Done,
  //     ReconWorkDone:          dealInfo?.recon_Work_Done,
  //     DoNotSell:              dealInfo?.do_Not_Sell,
  //     DoNotSellEndDate:       dealInfo?.do_Not_Sell_End_Date,
  //     DoNotSellReason:        dealInfo?.do_Not_Sell_Reason,
  //     NotArrivedYet:          dealInfo?.not_Arrived_Yet,
  //   };

  //   const newData = {
  //     ReportedToOEM:          reportedToOEM,
  //     UnclaimedRebates:       unclaimedRebates,
  //     RetailPriceInclVAT:     parseDecimal(retailPriceInclVAT),
  //     SellingPrice:           parseDecimal(sellingPrice),
  //     DisplayedDiscount:      parseDecimal(displayedDiscount),
  //     Notes:                  notes,
  //     PhotosTaken:            photosTaken,
  //     PhotosEdited:           photosEdited,
  //     // AutoTraderLink:         autoTraderLink,
  //     // AutoTraderPriceInclVAT: parseDecimal(autoTraderPriceInclVAT),
  //     DemoAvailableToSellDate: demoAvailableToSellDate,
  //     Check101Done:           check101Done,
  //     ReconWorkDone:          reconWorkDone,
  //     DoNotSell:              doNotSell,
  //     DoNotSellEndDate:       doNotSellEndDate,
  //     DoNotSellReason:        doNotSellReason,
  //     NotArrivedYet:          notArrivedYet,
  //   };

  //   if (!hasNoValue(dealInfo)) {
  //     console.log('vehicleStock oldData', oldData);
  //     console.log('vehicleStock newData', newData);
  //     const differences = getObjectDiff(oldData, newData);
  //     console.log('vehicleStock differences', differences);
  //     return differences;
  //   } else return [];
  // }

  async function insertDealAudit(differences) {
    /*
      public int PreloadedDealID { set; get; }
      public string UserEmail { set; get; }
      public string Operation { set; get; }
      public string Change { set; get; }
      public string OldValue { set; get; }
      public string NewValue { set; get; }
      public string DealType { set; get; }
      */
    differences.forEach(async diff => {
      const dataMap = {
        PreloadedDealID: dealInfo?.used_Car_ID,
        UserEmail: user?.emailAddress,
        Operation: diff?.type,
        Change: diff?.key,
        OldValue: diff?.old?.toString(),
        NewValue: diff?.new?.toString(),
        DealType: diff?.dealType
      }
      console.log('DealAudit CDP dataMap', dataMap);

      await api.insertDealAudit(dataMap);
    });
  }

  async function addToUpdateHistory(operation, change, oldValue, newValue) {
    const dataMap = {
        PreloadedDealID: dealInfo?.used_Car_ID,
        UserEmail: user?.emailAddress,
        Operation: operation,
        Change: change,
        OldValue: oldValue?.toString(),
        NewValue: newValue?.toString(),
        DealType: ""
      }
      console.log('DealAudit CDP dataMap', dataMap);

      await api.insertDealAudit(dataMap);
  }

  async function sendSRMEmail(isCreatingDeal, isDisplayOnWebsiteChanged) {
    let recipients = "";
    // switch (stockLocation + "_" + carType) {
    //   case `Carter Chery Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Willem.Fourie@heycarter.co.za,Anria.Lubbe@heycarter.co.za";
    //     break;

    //   case `Carter Chery Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Chery Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Ford Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Patrick.Scott@heycarter.co.za";
    //     break;

    //   case `Carter Ford Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Patrick.Scott@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Ford Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Patrick.Scott@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter GWM Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Neil.Adams@heycarter.co.za";
    //     break;

    //   case `Carter GWM Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Terry.Petzer@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter GWM Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Terry.Petzer@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Haval Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Neil.Adams@heycarter.co.za,Terry.Petzer@heycarter.co.za";
    //     break;

    //   case `Carter Haval Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Terry.Petzer@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Haval Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Terry.Petzer@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Mahindra Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Patrick.Scott@heycarter.co.za";
    //     break;

    //   case `Carter Mahindra Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Patrick.Scott@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Mahindra Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Patrick.Scott@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Mitsubishi Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Willem.Fourie@heycarter.co.za,Anria.Lubbe@heycarter.co.za";
    //     break;

    //   case `Carter Mitsubishi Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Mitsubishi Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Nissan Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Johan.Kunz@heycarter.co.za,Randy.Nel@heycarter.co.za";
    //     break;

    //   case `Carter Nissan Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Johan.Kunz@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Nissan Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Johan.Kunz@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Renault Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Neil.Adams@heycarter.co.za,Terry.Petzer@heycarter.co.za";
    //     break;

    //   case `Carter Renault Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Terry.Petzer@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Renault Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Terry.Petzer@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Suzuki Rosebank_${CONDITIONS.NEW}`:
    //     recipients = "Jaco.Niemand@heycarter.co.za";
    //     break;

    //   case `Carter Suzuki Rosebank_${CONDITIONS.DEMO}`:
    //     recipients = "Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Suzuki Rosebank_${CONDITIONS.USED}`:
    //     recipients = "Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Tata Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Johan.Kunz@heycarter.co.za,Randy.Nel@heycarter.co.za";
    //     break;

    //   case `Carter Tata Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Johan.Kunz@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Tata Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Johan.Kunz@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Volkswagen Bronkhorstspruit_${CONDITIONS.NEW}`:
    //     recipients = "Onicke.Schoombee@heycarter.co.za,Jaco.Reynders@heycarter.co.za";
    //     break;

    //   case `Carter Volkswagen Bronkhorstspruit_${CONDITIONS.DEMO}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Jaco.Reynders@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;

    //   case `Carter Volkswagen Bronkhorstspruit_${CONDITIONS.USED}`:
    //     recipients = "Anria.Lubbe@heycarter.co.za,Jaco.Reynders@heycarter.co.za,Willem.Fourie@heycarter.co.za,Tania.Coetzee@heycarter.co.za,Prudence.Pule@heycarter.co.za";
    //     break;
    
    //   default:
    //     break;
    // }
    const dataMap = {
      from: "hello@heycarter.co.za",
      to: "bhs.stockadmin@heycarter.com,hc.stockadmin@heycarter.com",
      replyTo: "",
      subject: `In-stock car ${isCreatingDeal ? "created" : "edited"} via deals portal. ${stockLocation}`,
      message: `
      <p>An in-stock car has been ${isCreatingDeal ? "added" : "edited"} via the deals portal.</p>
      ${(!isCreatingDeal && isDisplayOnWebsiteChanged) ? `<p>Is now <span style="color:${displayOnWebsite ? "green" : "red"};font-weight:bold;">${displayOnWebsite ? "DISPLAYED ON WEBSITE" : "NOT DISPLAYED ON WEBSITE"}</span></p>` : ""}
      
      <p>The stock location is: ${stockLocation}</p>
      
      <p>The details of the car are:</p>
      
      Car Type: ${carType}<br>
      Brand: ${brand}<br>
      Range: ${range}<br>
      Model: ${model}<br>
      MM code: ${mmCode}<br>
      Vin Number: ${vin}<br>
      Mileage: ${mileage}<br>
      Fuel type: ${fuelType}<br>
      Transmission: ${transmissionType}<br>
      Colour: ${colour}<br>
      Advertised Price: ${convertNumberToCurrency(sellingPrice - (displayedDiscount ?? 0))}<br>
      
      <p>In-stock Car Status: <span style="color:${displayOnWebsite ? "green" : "red"};font-weight:bold;">${displayOnWebsite ? "ACTIVE" : "INACTIVE"}</span></p>
      
      <p>Sellers comments:</p>
      
      <p>${notes?.length > 0 ? notes : "None"}</p>
      `
    }

    const emailResp = await api.sendEmail(dataMap);
    console.log('emailResp', emailResp);
    const isEmailSuccess = emailResp?.resultMessage === "Successfully saved";
    return isEmailSuccess;
  }

  const carterWebsiteLink = `https://heycarter.co.za/cardetails/${encodeURIComponent(brand)}/${encodeURIComponent(range)}/${encodeURIComponent(model)}%20(${encodeURIComponent(transmissionType)}%20%7C%20${encodeURIComponent(fuelType)})/${encodeURIComponent(modelID)}_used_${encodeURIComponent(dealInfo?.used_Car_ID)}`;

  const ExternalLink = ({children}) => (
    <div style={{display: "flex", gap: "2px", alignItems: "center"}}>
      {children} <Link />
    </div>
  );

  console.log("prepopulateItem", prepopulateItem);
  console.log("dealInfo", dealInfo);
  console.log('hasNoMMCodeResult', hasNoMMCodeResult);

  return (
    <Modal
      size={{ width: "95%", height: "auto" }}
      title={
        showMMCodeModal
          ? "Search MM Code"
          : showVinModal
          ? "Search VIN"
          : `You are now ${
              scope === SCOPES.EDIT ? "editing" : "creating"
            } a In-stock car deal ${scope === SCOPES.EDIT ? `(${dealInfo?.used_Car_ID})` : ""}`
      }
      icon={
        hasNoValue(dealInfo) ? (
          <Info size={20} color={colors.informational.color} />
        ) : (
          <Tooltip title="Deal update history">
            <IconButton onClick={() => setShowDealAuditHistory(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256">
                <path fill="currentColor" d="M136 80v43.47l36.12 21.67a8 8 0 0 1-8.24 13.72l-40-24A8 8 0 0 1 120 128V80a8 8 0 0 1 16 0m-8-48a95.44 95.44 0 0 0-67.92 28.15C52.81 67.51 46.35 74.59 40 82V64a8 8 0 0 0-16 0v40a8 8 0 0 0 8 8h40a8 8 0 0 0 0-16H49c7.15-8.42 14.27-16.35 22.39-24.57a80 80 0 1 1 1.66 114.75a8 8 0 1 0-11 11.64A96 96 0 1 0 128 32" />
              </svg>
            </IconButton >
          </Tooltip>
        )
      }
    >
      {/* <button onClick={() => {
        const onCarterWebsite = (displayOnWebsite);
        const carterWebsiteLink = `https://heycarter.co.za/cardetails/${encodeURIComponent(brand)}/${encodeURIComponent(range)}/${encodeURIComponent(model)}%20(${encodeURIComponent(transmissionType)}%20%7C%20${encodeURIComponent(fuelType)})/${encodeURIComponent(modelID)}_used_${encodeURIComponent(dealInfo?.used_Car_ID)}`;
        console.log("carterWebsiteLink", carterWebsiteLink);
      }}>Test</button> */}
      <ScrollEffect height="697px">
        {showMMCodeModal ? (
          <SearchModal
            searchFor="MMCode"
            newDemoUsed={newDemoUsed}
            value={mmCode}
            setValue={setMMCode}
            setResultItem={setPrepopulateItem}
            setShowSearchModal={setShowMMCodeModal}
            validate={requiredFieldsListPrice.mmCode.validate}
            showMMCodeModalAuto={showMMCodeModalAuto}
            setShowMMCodeModalAuto={setShowMMCodeModalAuto}
            setHasNoMMCodeResult={setHasNoMMCodeResult}
            addToUpdateHistory={addToUpdateHistory}
          />
        ) : showVinModal ? (
          <SearchModal
            searchFor="VIN"
            newDemoUsed={newDemoUsed}
            value={vin}
            setValue={setVin}
            setResultItem={setPrepopulateItem}
            setShowSearchModal={setShowVinModal}
            validate={requiredFieldsListPrice.vin.validate}
          />
        ) : (
          <div style={{padding: "8px 0", display: "grid", gap: "20px"}} ref={imageRef}>
            <div style={{display: "flex", gap: "20px"}}>
              <StackStyle padding="8px 0" gap="20px" alignContent="start" style={{flexGrow: 1}}>
                <div style={{display: "flex", gap: "8px"}}>
                  <div style={{display: "flex", gap: "8px", flexWrap: "wrap", flexGrow: 1}}>
                    {/* <SelectableBadge
                      label="Active deal"
                      selected={activeDeal}
                      onClick={() => setActiveDeal(!activeDeal)}
                    /> */}
                    <SelectableBadge
                      label="Display on website"
                      selected={displayOnWebsite}
                      onClick={() => {
                        const newValue = !displayOnWebsite;
                        if(newValue === true && newValue !== dealInfo?.display_On_Website) {
                          setListDate(listDateInit);
                          const [listDateInitUI] = getDate(new Date());
                          setListDateUI(listDateInitUI);
                        } else {
                          setListDate(dealInfo?.list_Date);
                          const [listDateInitUI] = getDate(dealInfo?.list_Date || "");
                          setListDateUI(listDateInitUI);
                        }
                        setDisplayOnWebsite(newValue)
                      }}
                    />
                    {/* <SelectableBadge
                      label="Actively advertise"
                      selected={activelyAdvertise}
                      onClick={() => setActivelyAdvertise(!activelyAdvertise)}
                    /> */}
                    <SelectableBadge
                      label="Reported to OEM"
                      selected={reportedToOEM}
                      onClick={() => setReportedToOEM(!reportedToOEM)}
                    />
                    <SelectableBadge
                      label="Photos taken"
                      selected={photosTaken}
                      onClick={() => setPhotosTaken(!photosTaken)}
                    />
                    <SelectableBadge
                      label="Photos edited"
                      selected={photosEdited}
                      onClick={() => setPhotosEdited(!photosEdited)}
                    />
                    {/* <SelectableBadge
                      label="DNS"
                      selected={doNotSell}
                      onClick={() => setDoNotSell(!doNotSell)}
                    /> */}
                    <SelectableBadge
                      label={<QueueStyle gap="8px">NAY <Lock /></QueueStyle>}
                      selected={notArrivedYet}
                      onClick={() => {}}
                    />
                    {carType === CONDITIONS.USED && (
                      <>
                      <SelectableBadge
                        label="101 check done"
                        selected={check101Done}
                        onClick={() => setCheck101Done(!check101Done)}
                      />
                      <SelectableBadge
                        label="Recon work done"
                        selected={reconWorkDone}
                        onClick={() => setReconWorkDone(!reconWorkDone)}
                      />
                      </>
                    )}
                  </div>
                  {displayOnWebsite && (
                    <ExternalLink>
                      <a href={carterWebsiteLink} target="_blank" rel="noreferrer">Carter Website</a>
                    </ExternalLink>
                  )}
                </div>
                <div style={{display: "grid", gap: "10px", justifyContent: "start", gridTemplateColumns: "160px auto auto auto auto"}}>
                  <MMCodeInput
                    value={mmCode}
                    setValue={setMMCode}
                    setShowMMCodeModal={setShowMMCodeModal}
                    validate={requiredFieldsListPrice.mmCode.validate}
                  />
                  <VINInput
                    value={vin}
                    setValue={setVin}
                    setShowVinModal={setShowVinModal}
                    validate={requiredFieldsListPrice.vin.validate}
                  />
                  <BrandSelect
                    brands={brands}
                    setBrand={setBrand}
                    setRange={setRange}
                    setModel={setModel}
                    fetchRanges={fetchRanges}
                    brand={brand}
                    isFetchingBrands={isFetchingBrands}
                    validate={requiredFieldsListPrice.brand.validate}
                  />
                  <RangeSelect
                    ranges={ranges}
                    setRange={setRange}
                    setModel={setModel}
                    fetchModels={fetchModels}
                    range={range}
                    isFetchingRanges={isFetchingRanges}
                    validate={requiredFieldsListPrice.range.validate}
                  />
                  <ModelSelectUsed
                    models={models}
                    setModel={setModel}
                    setPrepopulateItem={setPrepopulateItem}
                    model={model}
                    isFetchingModels={isFetchingModels}
                    validate={requiredFieldsListPrice.model.validate}
                  />
                </div>
                <QueueStyle gap="10px" justifyContent="stretch">
                  <CurrencyInput
                    label="Undiscounted selling price"
                    value={sellingPrice}
                    setValue={setSellingPrice}
                    initialValue={0}
                    suffix="(Incl VAT)"
                    validate={requiredFieldsListPrice.displayOnWebsite.validate} // Cannot advertise a low selling price vehicle
                  />
                  <CurrencyInput
                    label="Displayed Discount"
                    value={displayedDiscount}
                    setValue={setDisplayedDiscount}
                    suffix="(Incl VAT)"
                    validate={requiredFieldsListPrice.displayedDiscount.validate}
                  />
                  <CurrencyInputDisabled
                    label="Advertised selling price"
                    value={Math.floor(sellingPrice - displayedDiscount)}
                    suffix="(Incl VAT)"
                    validate={() => {}}
                  />
                  <CurrencyInput
                    label="Unclaimed Rebates"
                    value={unclaimedRebates}
                    setValue={setUnclaimedRebates}
                    suffix="(Incl VAT)"
                    validate={requiredFieldsListPrice.unclaimedRebates.validate}
                  />
                  {/* <CurrencyInput
                    label="Stand in value*"
                    value={standInValue}
                    setValue={setStandInValue}
                    initialValue={prepopulateItem?.standInValue}
                    suffix="R"
                    validate={requiredFieldsListPrice.standInValue.validate}
                  /> */}
                  
                </QueueStyle>
                <QueueStyle gap="10px" justifyContent="start">
                  <CurrencyInputDisabled
                    label="Retail price* (For New car)"
                    value={retailPriceExclVAT}
                    // setValue={setRetailPriceExclVAT}
                    suffix="(Excl VAT)"
                    validate={requiredFieldsListPrice.retailPriceExclVAT.validate}
                    // disabled={(!hasNoMMCodeResult)}
                  />
                  <CurrencyInputDisabled
                    label="Retail price* (For New car)"
                    value={retailPriceInclVAT}
                    // setValue={setRetailPriceInclVAT}
                    suffix="(Incl VAT)"
                    validate={requiredFieldsListPrice.retailPriceInclVAT.validate}
                    // disabled={(!hasNoMMCodeResult)}
                  />
                  {/* Display as empty for new entry cars from dealership stock prepopulation */}
                  {/* <InputDisabled label="First registration date*" value={new Date(dealInfo?.first_Reg_Date || "").toDateString()} /> */}
                  <DateInput
                    label="First registration date*"
                    value={firstRegDateUI}
                    setValue={setFirstRegDate}
                    setUIValue={setFirstRegDateUI}
                    validate={requiredFieldsListPrice.firstRegDate.validate}
                  />
                  <SelectInput
                    label="Stock location*"
                    options={stockLocations?.map(v => v.value)}
                    option={stockLocation}
                    setOption={setStockLocation}
                    validate={requiredFieldsListPrice.stockLocation.validate}
                  />
                  {/* <InputDisabled
                    label="Stock location*"
                    value={stockLocation}
                  /> */}
                  {/* <CurrencyInput
                    label="Deal assistance"
                    value={dealAssistance}
                    setValue={setDealAssistance}
                    suffix="R"
                    validate={() => ""}
                  /> */}
                </QueueStyle>
                {/* <QueueStyle gap="10px" justifyContent="start"> */}
                  {/* <DateInput
                    label="List date*"
                    value={listDateUI}
                    setValue={setListDate}
                    setUIValue={setListDateUI}
                    validate={requiredFieldsListPrice.listDate.validate}
                  /> */}
                  
                  {/* <SelectInput
                    label="Condition*"
                    options={Object.values(CAR_CONDITIONS)}
                    option={condition}
                    setOption={setCondition}
                    validate={requiredFieldsListPrice.condition.validate}
                  /> */}
                {/* </QueueStyle> */}
                <QueueStyle gap="10px" justifyContent="start">
                  <SelectInput
                    label="Transmission*"
                    options={Object.values(TRANSMISSION_TYPES)}
                    option={transmissionType}
                    setOption={setTransmissionType}
                    validate={requiredFieldsListPrice.transmission.validate}
                    disabled={(!hasNoMMCodeResult)}
                  />
                  <SelectInput
                    label="Fuel type*"
                    options={Object.values(FUEL_TYPES)}
                    option={fuelType}
                    setOption={setFuelType}
                    validate={requiredFieldsListPrice.fuelType.validate}
                    disabled={(!hasNoMMCodeResult)}
                  />
                  <SelectInput
                    label="Car type*"
                    options={Object.values(CONDITIONS)}
                    option={carType}
                    setOption={setCarType}
                    validate={requiredFieldsListPrice.carType.validate}
                  />
                  <NumberInput
                    label="Mileage*"
                    value={mileage}
                    setValue={setMileage}
                    suffix="km"
                    validate={requiredFieldsListPrice.mileage.validate}
                  />
                  <SelectInput
                    label="Colour*"
                    options={Object.values(COLOUR_TYPES)}
                    setOption={setColour}
                    option={colour}
                    prefix={
                      <Palette
                        size={14}
                        weight="thin"
                        color={
                          colour === COLOUR_TYPES.WHITE
                            ? "black"
                            : colour === COLOUR_TYPES.TBA
                            ? "black"
                            : colour.toLowerCase() || "black"
                        }
                      />
                    }
                    validate={requiredFieldsListPrice.colour.validate}
                  />
                </QueueStyle>
                

                {/* <QueueStyle gap="10px" justifyContent="start">
                  
                </QueueStyle> */}
                {carType?.toLowerCase() === CONDITIONS?.DEMO?.toLowerCase() && (
                  <QueueStyle gap="10px" justifyContent="start">
                    <DateInput
                      label="Demo available to sell*"
                      value={demoAvailableToSellDateUI}
                      setValue={setDemoAvailableToSellDate}
                      setUIValue={setDemoAvailableToSellDateUI}
                      validate={requiredFieldsListPrice.demoAvailableToSellDate.validate}
                    />
                  </QueueStyle>
                )}

                <QueueStyle gap="10px" justifyContent="start">
                  {/* <CurrencyInput
                    label="Estimated recon cost for car"
                    value={reconCost}
                    setValue={setReconCost}
                    suffix="R"
                    validate={() => ""}
                  /> */}
                  {/* <NumberInput
                    label="Recon time"
                    value={reconTime}
                    setValue={setReconTime}
                    suffix="(days)"
                    validate={() => ""}
                  /> */}
                  {!hasNoValue(autoTraderPriceInclVAT) && (
                    <CurrencyInputDisabled
                      label="Auto Trader price*"
                      value={autoTraderPriceInclVAT}
                      suffix="(Incl VAT)"
                      validate={() => ""}
                    />
                  )}
                  {!hasNoValue(autoTraderLink) && (
                    <ExternalLink>
                      <a href={autoTraderLink} target="_blank" rel="noreferrer">Auto Trader</a>
                    </ExternalLink>
                  )}
                </QueueStyle>
                <ToggleContainer isOpen={doNotSell} height="190px">
                  <div style={{display: "grid", gap:"20px"}}>
                    <Checkbox
                      label={<QueueStyle gap="8px">DNS <Lock /></QueueStyle>}
                      checked={doNotSell}
                      onChange={() => {}}
                    />
                    {doNotSell && (
                      <>
                      <DateInput
                        label="DNS end date*"
                        value={doNotSellEndDateUI}
                        setValue={setDoNotSellEndDate}
                        setUIValue={setDoNotSellEndDateUI}
                        validate={requiredFieldsListPrice.doNotSellEndDate.validate}
                      />
                      <TextAreaInput
                        label="DNS Reason"
                        value={doNotSellReason}
                        setValue={setDoNotSellReason}
                      />
                      </>
                    )}
                  </div>
                </ToggleContainer>
                {/* <ToggleContainer isOpen={includeInScheme} height="230px">
                  <StackStyle gap="20px" alignContent="start" width="max-content">
                    <Checkbox
                      label="Include scheme deal information"
                      checked={includeInScheme}
                      onChange={() => setIncludeInScheme(!includeInScheme)}
                    />
                    <QueueStyle gap="10px" justifyContent="start">
                      <DateTimeInput
                        label="Start date*"
                        value={startDateUI}
                        setValue={setStartDate}
                        setUIValue={setStartDateUI}
                        validate={requiredFieldsScheme.startDate.validate}
                      />
                      <DateTimeInput
                        label="End date*"
                        value={endDateUI}
                        setValue={setEndDate}
                        setUIValue={setEndDateUI}
                        validate={requiredFieldsScheme.endDate.validate}
                          />
                      <CurrencyInput
                        label="Advertised price*"
                        value={advertisedPrice}
                        setValue={setAdvertisedPrice}
                        initialValue={0}
                        suffix="R"
                        validate={requiredFieldsScheme.advertisedPrice.validate}
                      />
                    </QueueStyle>
                    <QueueStyle gap="10px" justifyContent="start">
                      <NumberInput
                        label="Rate*"
                        value={ratePercent}
                        setValue={setRatePercent}
                        suffix="%"
                        validate={requiredFieldsScheme.ratePercent.validate}
                      />
                      <SelectInput
                        label="Rate type*"
                        options={Object.values(RATE_TYPES)}
                        option={rateType}
                        setOption={setRateType}
                        validate={requiredFieldsScheme.rateType.validate}
                      />
                      <NumberInput
                        label="Deposit*"
                        value={deposit}
                        setValue={setDeposit}
                        suffix="%"
                        validate={requiredFieldsScheme.deposit.validate}
                      />
                      <NumberInput
                        label="Period*"
                        value={period}
                        setValue={setPeriod}
                        suffix="(Months)"
                        validate={requiredFieldsScheme.period.validate}
                      />
                    </QueueStyle>
                    <QueueStyle gap="10px" justifyContent="start">
                      <NumberInput
                        label="Balloon %*"
                        value={balloonPercent}
                        setValue={setBalloonPercent}
                        suffix="%"
                        validate={requiredFieldsScheme.balloonPercent.validate}
                      />
                      <CurrencyInput
                        label="Balloon*"
                        value={balloonAmount}
                        setValue={setBalloonAmount}
                        suffix="R"
                        validate={requiredFieldsScheme.balloonAmount.validate}
                      />
                          <CurrencyInputDisabled
                            label="Total cost of credit (Auto populated)"
                            value={totalCostOfCredit}
                            suffix="R"
                      />
                    </QueueStyle>
                  </StackStyle>
                </ToggleContainer> */}
                <QueueStyle gap="10px">
                  <TextAreaInput
                    label="Accessories or extras that will display on the advertised listing"
                    value={notes}
                    setValue={setNotes}
                  />
                  <Camera
                    size="2rem"
                    weight="thin"
                    onClick={() => setShowImagesGallery(true)}
                  />
                      {(allImages?.length || 0)}
                </QueueStyle>
              </StackStyle>
              <ImagesContainer maxWidth={"400px"}>
                <ScrollEffect height="452px">
                  <StandardImageList images={allImages} setImages={setAllImages} />
                </ScrollEffect>
              </ImagesContainer>
            </div>
            <QueueStyle justifyContent="center">
              <CancelButton onClick={popAll} />
              <SubmitButton
                disabled={!mayProceed || isSaving}
                onClick={() => {
                  // if (scope === SCOPES.CREATE) handleDuplicate();
                  // else if (scope === SCOPES.EDIT) handleSubmit();
                  handleSubmit();
                }}
                isSaving={isSaving}
              />
            </QueueStyle>
          </div>
        )}
      </ScrollEffect>
      <UsedCarImageGallery
        open={showImagesGallery}
        onClose={() => setShowImagesGallery(false)}
        images={allImages}
        setLocalImages={async (e) => {
          if (e?.target?.files === undefined) setLocalImages([]);
          const imageList = Object.keys(e.target.files).map((key) => e.target.files[key]);
          setLocalImages(imageList);
        }}
        setAllImages={setAllImages}
      />
      <DisplayOnWebsiteWarning
        open={showDisplayOnWebsiteWarning}
        onClose={() => setShowDisplayOnWebsiteWarning(false)}
        onConfirm={() => {
          setShowDisplayOnWebsiteWarning(false);
          //handleDuplicate();
          handleSubmit();
        }}
        displayOnWebsite={displayOnWebsite}
        setDisplayOnWebsite={() => setDisplayOnWebsite(!displayOnWebsite)}
        imagesInput={
          <ImagesInput
            value={localImages}
            setValue={async (e) => {
              if (e?.target?.files === undefined) setLocalImages([]);
              const byteArrayImages = await convertImageToByteArray(
                e.target.files
              );
              console.log("byteArrayImages:", byteArrayImages);
              setLocalImages(byteArrayImages);
            }}
          />
        }
      />
      <DealAuditHistory
        open={showDealAuditHistory}
        onClose={() => setShowDealAuditHistory(false)}
        dealInfo={dealInfo}
        idKey={"used_Car_ID"}
      />
      {/* <DuplicateDealFoundPopup
        open={showDuplicateCDPDealFound}
        onClose={() => {
          setShowDuplicateCDPDealFound(false);
        }}
        title="Duplicate List Price deal found"
        checked={shouldDuplicateCDPDeal}
        setChecked={setShouldDuplicateCDPDeal}
        onConfirm={() => {
          setShowDuplicateCDPDealFound(false);
          if (isPLDDuplicateDeal && includeInScheme) {
            console.log("show PLD duplicate");
            setShowDuplicatePLDDealFound(true);
          } else if (!isPLDDuplicateDeal || !includeInScheme) handleSubmit();
        }}
      /> */}
      {/* <DuplicateDealFoundPopup
        open={showDuplicatePLDDealFound}
        onClose={() => {
          setShowDuplicatePLDDealFound(false);
        }}
        title="Duplicate Scheme deal found"
        checked={shouldDuplicatePLDDeal}
        setChecked={setShouldDuplicatePLDDeal}
        onConfirm={() => {
          setShowDuplicatePLDDealFound(false);
          handleSubmit();
        }}
      /> */}
    </Modal>
  );
}

const ToggleContainer = styled.div`
  border: 1px solid lightgray;
  border-radius: 4px;
  padding: 10px;
  overflow-y: hidden;
  transition: 200ms ease-in-out;
  background-color: #f9f9f970;

  height: ${(props) => (props.isOpen ? props.height : "50px")};
`;

const ImagesContainer = styled.div`
  max-width: ${(props) => props.maxWidth || "unset"};
  padding: 0px 8px;
  display: none;
  @media (min-width: 1370px) {
    display: block;
  }
`;

// Map backend images
function mapBackendImages(backendImages = []) {
  return backendImages.map((img, index) => ({
    id: img.image, // Used for local ID
    imageID: img.imageID, // Backend image ID
    src: `${IMAGES_ENDPOINT}/${img?.image}?w=164&h=164&fit=crop&auto=format`, 
    isLocal: false,
    modified: false,
    backendPath: img.image,
    size: null,
    originalImageOrder: ++index
  }));
}

// Map local images
async function mapLocalImages(localImages = []) {
  return await Promise.all(localImages.map(async (img) => {
    const bytes = await img.arrayBuffer();
    const name = img.name;
    const ext = name.substring(name.lastIndexOf(".")).toLowerCase();

    const src = URL.createObjectURL(new Blob([bytes]));

    const size = getReadableSize(img.size);

    return {
      id: crypto.randomUUID(),
      imageID: null,
      src: src,
      isLocal: true,
      modified: true, // assume new local images are modified
      fileName: name,
      fileExt: ext,
      byteArray: bytes,
      size: size,
      tooLarge: (img.size / 1000) > 5000,
      originalImageOrder: undefined,
    }
  }));
}

// Batch images into groups below 5MB each
function batchImagesBySize(images = [], maxBatchSizeMB = 5) {
  const maxBatchSizeBytes = maxBatchSizeMB * 1024 * 1024;
  const batches = [];
  let currentBatch = [];
  let currentBatchSize = 0;

  images.forEach((image) => {
    const imageSize = image?.byteArray?.byteLength || 0;
    
    // If adding this image exceeds limit, start a new batch
    if (currentBatchSize + imageSize > maxBatchSizeBytes && currentBatch.length > 0) {
      batches.push([...currentBatch]);
      currentBatch = [];
      currentBatchSize = 0;
    }

    currentBatch.push(image);
    currentBatchSize += imageSize;
  });

  // Add remaining images
  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
}

export function getReadableSize(bytes = this) {
    if (bytes < 1024) return `${bytes} bytes`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  }